// Page layout v3 — tree of typed nodes (Container/Row/Column/Slot/Carousel)
// Bootstrap-grid-inspired:
//   Container > (Container | Row | Slot | Carousel)*
//   Row       > Column+                          (rows hold ONLY columns)
//   Column    > (Container | Row | Slot | Carousel)*  span 1..12 (12-col grid)
//   Slot      > leaf, ONE item from a group + preset (rendering style)
//   Carousel  > leaf, ALL items from a group + preset (one style for all slides)
//
// Free nesting: a column can hold another row, a row can sit inside a
// container inside a column, etc. Hydrated shape mirrors the tree but
// replaces leaves (Slot/Carousel) with their hydrated counterparts.

import type { HydratedGroupItem } from './group-content';

/* ---------- Node primitives ---------- */

export const COLUMN_SPAN_MAX = 12 as const;
export const ROW_GAP_MAX = 5 as const;
export const CONTAINER_CHILDREN_MAX = 20 as const;
export const COLUMN_CHILDREN_MAX = 20 as const;
export const ROW_COLUMNS_MAX = 12 as const;

/** Container layout variants — carousel moved to its own node type. */
export type ContainerVariant = 'fluid' | 'fixed';

/** 1..12 enforced at runtime via Zod min/max — keeping as `number` here
 *  avoids fighting TS variance in the recursive layout schema. */
export type ColumnSpan = number;
/** 0..5 enforced at runtime — see ColumnSpan note. */
export type RowGap = number;

/** Rendering presets — single source of truth for both Slot and Carousel.
 *  Enum-validated at the Zod schema level. Mirrors VARIANT_PRESETS in
 *  apps/web/src/lib/layout-variants.ts. */
export const SLOT_PRESETS = [
  'hero',
  'card',
  'featured',
  'thumb-sm',
  'thumb-md',
  'thumb-lg',
  'compact',
] as const;
export type SlotPreset = (typeof SLOT_PRESETS)[number];

/** Heading sizes for Label nodes — mirrors HTML h1..h6. The public
 *  renderer should map each value to the matching `<hN>` tag. */
export const LABEL_PRESETS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
export type LabelPreset = (typeof LABEL_PRESETS)[number];

export interface BaseNode {
  id: string;
}

export interface ContainerNode extends BaseNode {
  type: 'container';
  variant: ContainerVariant;
  children: ContainerChild[];
}

export interface RowNode extends BaseNode {
  type: 'row';
  gap: RowGap;
  /** Rows hold ONLY columns (sum of span ≤ 12, enforced by Zod refine). */
  children: ColumnNode[];
}

export interface ColumnNode extends BaseNode {
  type: 'column';
  span: ColumnSpan;
  children: ColumnChild[];
}

export interface SlotNode extends BaseNode {
  type: 'slot';
  /** References group_contents.slug. */
  groupSlug: string;
  /** Which item from the group this slot renders. 0-based position in
   *  the group's hydrated items list. Defaults to 0 in the editor. If
   *  the group has fewer items than the index requests, slot.item will
   *  be `null` at hydration time. */
  itemIndex: number;
  /** Rendering preset for THIS slot's single item. */
  preset: SlotPreset;
}

export interface CarouselNode extends BaseNode {
  type: 'carousel';
  /** References group_contents.slug. Carousel takes the full items list (up to maxCount). */
  groupSlug: string;
  /** Rendering preset applied to every slide. */
  preset: SlotPreset;
}

export interface LabelNode extends BaseNode {
  type: 'label';
  /** Static text to display. */
  text: string;
  /** Optional URL — when set, public renderer wraps the heading in an `<a>`. */
  url?: string;
  /** Heading size — public renderer emits the matching `<hN>` tag. */
  preset: LabelPreset;
}

/** Children allowed inside a Container or Column (anything except a bare
 *  Column — columns only exist as direct children of a Row). */
export type ContainerChild = ContainerNode | RowNode | SlotNode | CarouselNode | LabelNode;
export type ColumnChild = ContainerNode | RowNode | SlotNode | CarouselNode | LabelNode;

export type LayoutNode = ContainerNode | RowNode | ColumnNode | SlotNode | CarouselNode | LabelNode;

/* ---------- Top-level page layout ---------- */

export interface PageLayout {
  version: 3;
  /** Root is always a Container; admin cannot delete it. Stable id 'root'. */
  root: ContainerNode;
}

/* ---------- Hydrated shape (public response) ---------- */

export interface HydratedContainerNode extends BaseNode {
  type: 'container';
  variant: ContainerVariant;
  children: HydratedContainerChild[];
}

export interface HydratedRowNode extends BaseNode {
  type: 'row';
  gap: RowGap;
  children: HydratedColumnNode[];
}

export interface HydratedColumnNode extends BaseNode {
  type: 'column';
  span: ColumnSpan;
  children: HydratedColumnChild[];
}

export interface HydratedSlotNode extends BaseNode {
  type: 'slot';
  groupSlug: string;
  groupName: string;
  groupType: 'article' | 'video' | 'url' | 'streaming' | 'customized';
  preset: SlotPreset;
  /** Single item from the group (assigned by DFS-order consumption across
   *  all slots referencing this group on the page). `null` when the group
   *  has fewer items than the slots pointing at it. */
  item: HydratedGroupItem | null;
  /** True when `item` came from an active slot override (ข่าวประชาสัมพันธ์)
   *  instead of the group's normal item. Public renderer can use this to
   *  decorate (ribbon, etc.); admin live preview adds a 📣 badge. Absent
   *  (undefined) when no override is active. */
  isOverride?: true;
}

export interface HydratedCarouselNode extends BaseNode {
  type: 'carousel';
  groupSlug: string;
  groupName: string;
  groupType: 'article' | 'video' | 'url' | 'streaming' | 'customized';
  preset: SlotPreset;
  /** All items in the group (admin order, capped at group.maxCount). Every
   *  slide renders with the same `preset`. When a slot override is active
   *  for slide N, `items[N]` is the PR content instead of the group's item
   *  and N appears in `overrideSlideIndexes`. */
  items: HydratedGroupItem[];
  /** Sorted ascending list of slide positions currently substituted by a
   *  PR (ข่าวประชาสัมพันธ์). Empty/absent = no active overrides. Public
   *  renderer can use this to decorate individual slides with a ribbon. */
  overrideSlideIndexes?: number[];
}

export interface HydratedLabelNode extends BaseNode {
  type: 'label';
  text: string;
  /** Null instead of undefined to keep the hydrated shape strict — admin
   *  may leave url empty; public renderer just doesn't wrap in `<a>`. */
  url: string | null;
  preset: LabelPreset;
}

export type HydratedContainerChild =
  | HydratedContainerNode
  | HydratedRowNode
  | HydratedSlotNode
  | HydratedCarouselNode
  | HydratedLabelNode;

export type HydratedColumnChild = HydratedContainerChild;

export type HydratedLayoutNode =
  | HydratedContainerNode
  | HydratedRowNode
  | HydratedColumnNode
  | HydratedSlotNode
  | HydratedCarouselNode
  | HydratedLabelNode;

export interface PublicPageResponse {
  page: {
    slug: string;
    name: string;
    description: string | null;
  };
  version: 3;
  /** Always a HydratedContainerNode (mirrors PageLayout.root). */
  root: HydratedContainerNode;
  generatedAt: string;
}

/* ---------- Public datas (layout-less) shape ---------- */

/** One Group Content entry hydrated with its items. Returned by
 *  `/api/public/pages/:slug/datas` — no layout structure, just the data
 *  the page's slots reference. Distinct groups only (deduped by slug);
 *  if the same group is referenced by N slots, it appears once. */
export interface PublicPageGroupEntry {
  groupSlug: string;
  groupName: string;
  groupType: 'article' | 'video' | 'url' | 'streaming' | 'customized';
  mode: 'auto' | 'manual';
  items: HydratedGroupItem[];
}

export interface PublicPageDatasResponse {
  page: {
    slug: string;
    name: string;
    description: string | null;
  };
  version: 3;
  /** Distinct groups referenced by the page's slots/carousels, ordered DFS
   *  pre-order by first occurrence in the layout tree (= visual reading
   *  order). NOT deduped at the item level — each group reports its full
   *  item list regardless of overlap with other groups. */
  groups: PublicPageGroupEntry[];
  generatedAt: string;
}

/* ---------- Empty defaults ---------- */

export const EMPTY_ROOT_CONTAINER: ContainerNode = {
  id: 'root',
  type: 'container',
  variant: 'fluid',
  children: [],
};

export const EMPTY_PAGE_LAYOUT: PageLayout = {
  version: 3,
  root: EMPTY_ROOT_CONTAINER,
};

// Backward-compat alias `PublicHomepageResponse` lives in ./homepage to
// avoid a duplicate re-export here.
